"use client";

import { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";

import { useAuth } from "@/components/providers/auth-provider";
import { isHelpLauncherHiddenRoute } from "@/lib/full-viewport-routes";

const HelpLauncher = dynamic(() => import("@/features/support/components/HelpLauncher").then((m) => m.HelpLauncher), {
  ssr: false,
});

/**
 * `SUP-AI03`: the help launcher's place on every page (`docs/38` §3). It
 * stays off the first paint: the launcher's code loads after the page has
 * loaded and the browser is idle (the way `LazyToaster` does), and the chat
 * panel only when the person reaches for it. Not on the routes
 * `isHelpLauncherHiddenRoute` names, and not for a contest login, which lives
 * on its contest's pages and asks there.
 */
export function HelpLauncherSlot() {
  const pathname = usePathname() ?? "";
  const { user } = useAuth();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let idle: number | null = null;
    const whenIdle = () => {
      if ("requestIdleCallback" in window) idle = window.requestIdleCallback(() => setReady(true), { timeout: 3000 });
      else setReady(true);
    };
    if (document.readyState === "complete") whenIdle();
    else window.addEventListener("load", whenIdle, { once: true });
    return () => {
      window.removeEventListener("load", whenIdle);
      if (idle !== null) window.cancelIdleCallback(idle);
    };
  }, []);

  if (!ready || isHelpLauncherHiddenRoute(pathname) || user?.accountKind === "contest_temp") return null;
  return <HelpLauncher />;
}
